import { useQuery } from '@apollo/client/react'
import { SearchSelect } from '@src/components/Elements/SearchSelect'
import React, { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PlotNodeConnection } from '@src/generated/schema'
import { SearchSelectProps } from '@src/components/Elements/SearchSelect/SearchSelect'
import debounce from 'lodash.debounce'
import { Plot } from '@src/graphql/Plot'

interface PlotSelectProps extends Pick<SearchSelectProps, 'required'> {
  prefilledValue?: number
}

export const PlotSelect: FC<PlotSelectProps> = ({ prefilledValue, ...props }) => {
  const { t } = useTranslation()
  const { data, loading, fetchMore, refetch } = useQuery<{ plots: PlotNodeConnection }>(Plot.searchPlots, {
    variables: {
      first: 10,
      plotNumber: prefilledValue,
    },
    fetchPolicy: 'network-only',
  })
  const { plots: { edges } = { edges: [] as any } } = data || {}

  const handleSearch = useCallback(
    debounce((value: string) => {
      refetch({
        first: 10,
        plotNumber: value,
      })
    }, 100),
    [],
  )

  const handlePopupScroll = (e: any) => {
    e.persist()
    const scrollTop = e.target.scrollTop as number
    const offsetHeight = e.target.offsetHeight as number

    if (scrollTop + offsetHeight === e.target.scrollHeight) {
      fetchMore({
        variables: {
          after: data?.plots.pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          const newEdges = fetchMoreResult.plots.edges
          const pageInfo = fetchMoreResult.plots.pageInfo

          return newEdges.length
            ? {
                plots: {
                  pageInfo,
                  __typename: prev.plots.__typename,
                  edges: [...prev.plots.edges, ...newEdges],
                },
              }
            : prev
        },
      })
    }
  }

  return (
    <SearchSelect
      {...props}
      allowClear
      label={t('general.pile.plotNumber')}
      options={edges.map(({ node: { number } }) => ({
        title: number,
        value: number,
      }))}
      name='plotNumber'
      loading={loading}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
    />
  )
}
